/**
 * Strategy Adjustment Engine service — talks to
 * `/api/v1/strategy-adjustment/{symbol}/*` (see
 * `app.api.v1.strategy_adjustment`). Lets a trader manually modify a
 * recommendation `strategyRecommendationService` already produced, before
 * anything reaches a broker.
 *
 * EXECUTION SAFETY: there is deliberately no `execute(...)` method here —
 * this service can only synthesize legs, recalculate metrics, simulate
 * scenarios, validate, and preview. No method in this file calls, or could
 * call, a broker order endpoint; real order placement is a separate
 * service (`executionService.ts`) behind `ExecutionPanel`'s own single
 * "Confirm & Place Order" confirmation, not anything exposed here.
 * `executionPreview()`/`confirmationSummary()` below are no longer called
 * from `StrategyAdjustmentPage.tsx` (their only UI consumers were removed
 * as redundant with `ExecutionPanel`'s own preview/confirmation step), but
 * remain available for future reuse.
 */

import api from "@/services/api";
import type {
  AdjustableStrategyType,
  ConfirmationSummaryRead,
  ExecutionPreviewResponse,
  OptionLeg,
  OriginalRecommendationRead,
  PayoffCurveRead,
  RecalculatedMetricsRead,
  SimulationResultRead,
  SynthesizeLegsResponse,
  ValidationResultRead,
} from "@/types";

export const strategyAdjustmentService = {
  async getOriginal(symbol: string): Promise<OriginalRecommendationRead> {
    const { data } = await api.get<OriginalRecommendationRead>(`/strategy-adjustment/${symbol}/original`);
    return data;
  },

  async synthesize(symbol: string, strategyKey: AdjustableStrategyType): Promise<SynthesizeLegsResponse> {
    const { data } = await api.post<SynthesizeLegsResponse>(`/strategy-adjustment/${symbol}/synthesize`, {
      strategy_key: strategyKey,
    });
    return data;
  },

  async recalculate(
    symbol: string,
    legs: OptionLeg[],
    underlyingPriceOverride?: number | null
  ): Promise<RecalculatedMetricsRead> {
    const { data } = await api.post<RecalculatedMetricsRead>(`/strategy-adjustment/${symbol}/recalculate`, {
      legs,
      underlying_price_override: underlyingPriceOverride ?? null,
    });
    return data;
  },

  async simulate(
    symbol: string,
    legs: OptionLeg[],
    underlyingPriceOverride?: number | null
  ): Promise<SimulationResultRead> {
    const { data } = await api.post<SimulationResultRead>(`/strategy-adjustment/${symbol}/simulate`, {
      legs,
      underlying_price_override: underlyingPriceOverride ?? null,
    });
    return data;
  },

  async payoffCurve(
    symbol: string,
    legs: OptionLeg[],
    underlyingPriceOverride?: number | null
  ): Promise<PayoffCurveRead> {
    const { data } = await api.post<PayoffCurveRead>(`/strategy-adjustment/${symbol}/payoff-curve`, {
      legs,
      underlying_price_override: underlyingPriceOverride ?? null,
    });
    return data;
  },

  async validate(
    symbol: string,
    legs: OptionLeg[],
    brokerAccountId?: string,
    underlyingPriceOverride?: number | null
  ): Promise<ValidationResultRead> {
    const { data } = await api.post<ValidationResultRead>(`/strategy-adjustment/${symbol}/validate`, {
      legs,
      broker_account_id: brokerAccountId ?? null,
      underlying_price_override: underlyingPriceOverride ?? null,
    });
    return data;
  },

  async executionPreview(symbol: string, legs: OptionLeg[]): Promise<ExecutionPreviewResponse> {
    const { data } = await api.post<ExecutionPreviewResponse>(
      `/strategy-adjustment/${symbol}/execution-preview`,
      { legs }
    );
    return data;
  },

  async confirmationSummary(
    symbol: string,
    originalStrategyKey: string,
    legs: OptionLeg[],
    underlyingPriceOverride?: number | null
  ): Promise<ConfirmationSummaryRead> {
    const { data } = await api.post<ConfirmationSummaryRead>(
      `/strategy-adjustment/${symbol}/confirmation-summary`,
      { original_strategy_key: originalStrategyKey, legs, underlying_price_override: underlyingPriceOverride ?? null }
    );
    return data;
  },
};

export default strategyAdjustmentService;
